/**
 * /gebieden — beheer van de prijs- en gebiedsconfiguratie (SF-exit kaart 18
 * fase 3, plan plans/2026-08-02-sf-exit-kaart-18-prijs-gebied-config.md).
 *
 * Vervangt de SF-UI-handelingen op de vijf config-objecten. Leest via
 * GET /api/config/overzicht (de mirrors) en schrijft uitsluitend via de
 * fase 2-verbs (POST /api/config/*) — de guards daar zijn de waarheid, de
 * UI toont hun foutmeldingen letterlijk.
 *
 * Twee secties:
 *  - Routes: capaciteit ophalen/afleveren per route-definitie (verb
 *    route-capaciteit).
 *  - Gebieden: per postcodegebied de range (range-wijzigen), de gekoppelde
 *    routes met tijdvenster (rpa-aanmaken/-verhangen/-verwijderen) en de
 *    regiotoeslag + order-minimum per DPA (dpa-toeslag-zetten, incl. de
 *    terugwerkend-bevestiging die de guard kan eisen).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '../components/ui';
import { supabase } from '../lib/supabase';
import { formatStreefduur, parseStreefduur } from '../lib/route-streefduur';

// ─── Types (spiegel van GET /api/config/overzicht) ─────────────

interface RouteDef {
  sf_id: string;
  name: string | null;
  beschrijving: string | null;
  capaciteit_ophalen: number | null;
  capaciteit_afleveren: number | null;
  dag_budget_minuten: number | null;
}

interface Dienstgebied {
  sf_id: string;
  name: string | null;
  segment: string | null;
}

interface RangeRij {
  sf_id: string;
  name: string | null;
  postcode_begin: number | null;
  postcode_einde: number | null;
  regiotoeslag: boolean | null;
  gebieden: string | null;
  gesloten_van: string | null;
  gesloten_tot: string | null;
  gesloten_reden: string | null;
}

interface DpaRij {
  sf_id: string;
  name: string | null;
  dienstgebied_id: string | null;
  postcode_gebied_id: string | null;
  partner_id: string | null;
  segment: string | null;
  regiotoeslag: boolean | null;
  toeslag: number | null;
  toeslag_ingangsdatum: string | null;
  minimum_order_excl_btw: number | null;
  afhandelingsmethode: string | null;
}

interface PartnerRij {
  sf_id: string;
  name: string | null;
}

interface RpaRij {
  sf_id: string;
  name: string | null;
  route_id: string | null;
  postcodegebied_id: string | null;
  tijd_vanaf: string | null;
  tijd_tot: string | null;
}

interface Overzicht {
  routes: RouteDef[];
  gebieden: Dienstgebied[];
  ranges: RangeRij[];
  dpas: DpaRij[];
  rpas: RpaRij[];
  partners: PartnerRij[];
}

// ─── API helpers ───────────────────────────────────────────────

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

async function haalOverzicht(): Promise<Overzicht> {
  const res = await fetch('/api/config/overzicht', { headers: await authHeaders() });
  const body = await res.json();
  if (!res.ok || !body.success) throw new Error(body.error ?? `Overzicht laden faalde (${res.status})`);
  return body.data as Overzicht;
}

/** Voer een config-verb uit; gooit met de guard-melding bij afwijzing. */
async function voerVerbUit(verb: string, payload: Record<string, unknown>): Promise<unknown> {
  const res = await fetch(`/api/config/${verb}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  const body = await res.json();
  if (!res.ok || !body.success) {
    const err = new Error(body.error ?? `Wijziging faalde (${res.status})`);
    (err as Error & { code?: string }).code = body.code;
    throw err;
  }
  return body.data;
}

const tijdKort = (t: string | null): string => (t ? t.slice(0, 5) : '—');
const euro = (v: number | null): string =>
  v === null ? '—' : `€${v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Modal-schil ───────────────────────────────────────────────

function ModalShell({ titel, onClose, children, breed = false }: { titel: string; onClose: () => void; children: React.ReactNode; breed?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`relative bg-white rounded-lg shadow-xl w-full ${breed ? 'max-w-2xl' : 'max-w-lg'} mx-4 max-h-[85vh] flex flex-col`}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{titel}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Sluiten">×</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function FoutBanner({ melding }: { melding: string | null }) {
  if (!melding) return null;
  return (
    <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
      {melding}
    </div>
  );
}

const veldKlasse =
  'w-full h-10 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent';
const labelKlasse = 'block text-xs font-medium text-gray-600 mb-1';

// ─── Capaciteit-modal (route-capaciteit) ───────────────────────

function StreefduurModal({ route, onClose, onSaved }: { route: RouteDef; onClose: () => void; onSaved: () => void }) {
  const [duur, setDuur] = useState(formatStreefduur(route.dag_budget_minuten));
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const opslaan = async () => {
    setBezig(true); setFout(null);
    try {
      await voerVerbUit('route-streefduur', { routeId: route.sf_id, dagBudgetMinuten: parseStreefduur(duur) });
      onSaved();
    } catch (e) { setFout(e instanceof Error ? e.message : String(e)); setBezig(false); }
  };
  return <ModalShell titel={`Streefduur routedag · ${route.name ?? route.sf_id}`} onClose={onClose}>
    <FoutBanner melding={fout} />
    <label htmlFor="route-streefduur" className={labelKlasse}>Streefduur routedag (uren:minuten)</label>
    <input id="route-streefduur" className={veldKlasse} value={duur} placeholder="10:00" onChange={e => setDuur(e.target.value)} />
    <p className="mt-2 text-sm text-gray-500">Geldt voor alle routedagen van deze route, ook bestaande. Onder deze duur kan automatisch één extra plek vrijkomen. De laatste boeking mag eroverheen gaan. Leeg = 10 uur.</p>
    <div className="mt-5 flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Annuleren</Button>
      <Button onClick={opslaan} isLoading={bezig}>Opslaan</Button>
    </div>
  </ModalShell>;
}

function CapaciteitModal({ route, onClose, onSaved }: { route: RouteDef; onClose: () => void; onSaved: () => void }) {
  const [ophalen, setOphalen] = useState(String(route.capaciteit_ophalen ?? ''));
  const [afleveren, setAfleveren] = useState(String(route.capaciteit_afleveren ?? ''));
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { routeId: route.sf_id };
      if (ophalen !== '') payload.capaciteitOphalen = Number(ophalen);
      if (afleveren !== '') payload.capaciteitAfleveren = Number(afleveren);
      await voerVerbUit('route-capaciteit', payload);
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezig(false);
    }
  };

  return (
    <ModalShell titel={`Capaciteit — ${route.name ?? route.sf_id}`} onClose={onClose}>
      <FoutBanner melding={fout} />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelKlasse}>Capaciteit ophalen (stops)</label>
          <input type="number" min={0} max={99} className={veldKlasse} value={ophalen} onChange={(e) => setOphalen(e.target.value)} />
        </div>
        <div>
          <label className={labelKlasse}>Capaciteit afleveren (stops)</label>
          <input type="number" min={0} max={99} className={veldKlasse} value={afleveren} onChange={(e) => setAfleveren(e.target.value)} />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button onClick={opslaan} isLoading={bezig}>Opslaan</Button>
      </div>
    </ModalShell>
  );
}

// ─── Range-modal (range-wijzigen) ──────────────────────────────

function RangeModal({ range, onClose, onSaved }: { range: RangeRij; onClose: () => void; onSaved: () => void }) {
  const [naam, setNaam] = useState(range.name ?? '');
  const [begin, setBegin] = useState(String(range.postcode_begin ?? ''));
  const [einde, setEinde] = useState(String(range.postcode_einde ?? ''));
  const [geslotenVan, setGeslotenVan] = useState(range.gesloten_van ?? '');
  const [geslotenTot, setGeslotenTot] = useState(range.gesloten_tot ?? '');
  const [geslotenReden, setGeslotenReden] = useState(range.gesloten_reden ?? '');
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { rangeId: range.sf_id };
      if (naam !== (range.name ?? '')) payload.naam = naam;
      if (begin !== String(range.postcode_begin ?? '')) payload.postcodeBegin = Number(begin);
      if (einde !== String(range.postcode_einde ?? '')) payload.postcodeEinde = Number(einde);
      // Venster is alles-of-niets (guard 5): raakt één veld, stuur alle drie
      // ('' → null zodat legen werkt).
      const vensterGeraakt =
        geslotenVan !== (range.gesloten_van ?? '') ||
        geslotenTot !== (range.gesloten_tot ?? '') ||
        geslotenReden !== (range.gesloten_reden ?? '');
      if (vensterGeraakt) {
        payload.geslotenVan = geslotenVan || null;
        payload.geslotenTot = geslotenTot || null;
        payload.geslotenReden = geslotenReden.trim() || null;
      }
      if (Object.keys(payload).length === 1) { onClose(); return; }
      await voerVerbUit('range-wijzigen', payload);
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezig(false);
    }
  };

  return (
    <ModalShell titel={`Gebied bewerken — ${range.name ?? range.sf_id}`} onClose={onClose}>
      <FoutBanner melding={fout} />
      <div className="space-y-4">
        <div>
          <label className={labelKlasse}>Naam</label>
          <input className={veldKlasse} value={naam} onChange={(e) => setNaam(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelKlasse}>Postcode vanaf</label>
            <input type="number" min={1000} max={9999} className={veldKlasse} value={begin} onChange={(e) => setBegin(e.target.value)} />
          </div>
          <div>
            <label className={labelKlasse}>Postcode t/m</label>
            <input type="number" min={1000} max={9999} className={veldKlasse} value={einde} onChange={(e) => setEinde(e.target.value)} />
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div>
            <div className="text-xs font-semibold text-slate-700">Tijdelijk gesloten (sluitingsvenster)</div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              In deze periode wordt het gebied niet aan klanten voorgesteld (offerte-mails, mail-link,
              client-app). Kantoor kan wél plannen — die dagen krijgen een "gebied dicht"-markering.
              Loopt vanzelf af. Alles-of-niets: beide datums plus reden, of alles leeg.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelKlasse}>Gesloten van</label>
              <input type="date" className={veldKlasse} value={geslotenVan} onChange={(e) => setGeslotenVan(e.target.value)} />
            </div>
            <div>
              <label className={labelKlasse}>Gesloten t/m</label>
              <input type="date" className={veldKlasse} value={geslotenTot} onChange={(e) => setGeslotenTot(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelKlasse}>Reden (verplicht bij een venster)</label>
            <input
              className={veldKlasse}
              value={geslotenReden}
              onChange={(e) => setGeslotenReden(e.target.value)}
              placeholder="Bijv. Algerabrug dicht t/m 7 sept"
            />
          </div>
          {(geslotenVan || geslotenTot) && (
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-red-600 underline"
              onClick={() => { setGeslotenVan(''); setGeslotenTot(''); setGeslotenReden(''); }}
            >
              Venster wissen (gebied weer voorstellen)
            </button>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button onClick={opslaan} isLoading={bezig}>Opslaan</Button>
      </div>
    </ModalShell>
  );
}

// ─── Segment-helpers ───────────────────────────────────────────

/**
 * Route-koppelingen bedienen de eigen ophaal/bezorg-logistiek en dus het
 * Wasserij-segment (Kleda); Dakkapel, Zonnepanelen en SR lopen via partners
 * die zelf rijden. "Route maar geen Wasserij-prijs" is daarom een echte
 * inconsistentie en geen ruis — meting 05-08: 180 gebieden hebben beide,
 * 14 geen van beide, 4 staan scheef (waaronder Zundert).
 */
const ROUTE_SEGMENT = 'Wasserij';

/**
 * Segmenten die op minstens zoveel gebieden voorkomen krijgen in élke rij een
 * chip, ook waar ze ontbreken — juist dat gat is de informatie. Zeldzame
 * segmenten (Matras, Algemeen) tonen alleen op de gebieden die ze hebben,
 * anders staat de tabel vol met lege chips.
 */
const KERN_SEGMENT_DREMPEL = 20;

/** Meest voorkomende niet-lege waarde; gebruikt om erf-waarden voor te stellen. */
function modus<T>(waarden: (T | null | undefined)[]): T | null {
  const tel = new Map<string, { waarde: T; n: number }>();
  for (const w of waarden) {
    if (w === null || w === undefined || w === '') continue;
    const sleutel = String(w);
    const rij = tel.get(sleutel) ?? { waarde: w, n: 0 };
    rij.n += 1;
    tel.set(sleutel, rij);
  }
  let best: { waarde: T; n: number } | null = null;
  for (const rij of tel.values()) if (!best || rij.n > best.n) best = rij;
  return best?.waarde ?? null;
}

/** Wat een nieuwe DPA in dit dienstgebied van zijn siblings zou erven. */
interface ErfWaarden {
  partnerId: string | null;
  afhandelingsmethode: string | null;
  minimum: number | null;
  aantalSiblings: number;
}

function bepaalErfWaarden(alleDpas: DpaRij[], dienstgebiedId: string): ErfWaarden {
  const siblings = alleDpas.filter((d) => d.dienstgebied_id === dienstgebiedId);
  return {
    partnerId: modus(siblings.map((d) => d.partner_id)),
    afhandelingsmethode: modus(siblings.map((d) => d.afhandelingsmethode)),
    minimum: modus(siblings.map((d) => d.minimum_order_excl_btw)),
    aantalSiblings: siblings.length,
  };
}

// ─── Segment openstellen (dpa-aanmaken, met dry-run-voorbeeld) ──

function OpenstellenVorm({
  range,
  dienstgebied,
  erf,
  partnerNaam,
  onGedaan,
  onAnnuleer,
}: {
  range: RangeRij;
  dienstgebied: Dienstgebied;
  erf: ErfWaarden;
  partnerNaam: (id: string | null) => string;
  onGedaan: () => void;
  onAnnuleer: () => void;
}) {
  const [minimum, setMinimum] = useState(erf.minimum !== null ? String(erf.minimum) : '');
  const [voorbeeld, setVoorbeeld] = useState<Record<string, unknown> | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const payload = (): Record<string, unknown> => {
    const p: Record<string, unknown> = {
      postcodegebiedId: range.sf_id,
      dienstgebiedId: dienstgebied.sf_id,
    };
    if (minimum !== '') p.minimumOrderExclBtw = Number(minimum);
    return p;
  };

  // Stap 1: dry-run. De guards draaien echt (divergentie, partner-erving), maar
  // er wordt niets weggeschreven — pas na akkoord op wat de server terugmeldt.
  const controleren = async () => {
    setBezig(true);
    setFout(null);
    try {
      const data = (await voerVerbUit('dpa-aanmaken', { ...payload(), dryRun: true })) as {
        voorgenomen?: Record<string, unknown>;
      };
      setVoorbeeld(data?.voorgenomen ?? {});
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setBezig(false);
    }
  };

  const bevestigen = async () => {
    setBezig(true);
    setFout(null);
    try {
      await voerVerbUit('dpa-aanmaken', payload());
      onGedaan();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezig(false);
    }
  };

  const geenPartner = erf.partnerId === null;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
      <FoutBanner melding={fout} />
      {geenPartner ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Er is nog geen enkele prijsassociatie in dienstgebied "{dienstgebied.name}", dus er valt
          geen partner te erven. Richt dit dienstgebied eerst in Salesforce in.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelKlasse}>Order-minimum (excl. btw)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className={veldKlasse}
                value={minimum}
                onChange={(e) => { setMinimum(e.target.value); setVoorbeeld(null); }}
              />
              {erf.minimum !== null && (
                <p className="mt-1 text-[11px] text-gray-400">
                  Gangbaar in dit dienstgebied: {euro(erf.minimum)}
                </p>
              )}
            </div>
            <div className="text-xs text-gray-600 space-y-1 pt-5">
              <div>Partner: <span className="text-gray-900">{partnerNaam(erf.partnerId)}</span></div>
              <div>Afhandeling: <span className="text-gray-900">{erf.afhandelingsmethode ?? '—'}</span></div>
              <div className="text-gray-400">Overgenomen van {erf.aantalSiblings} ander(e) gebied(en)</div>
            </div>
          </div>
          <p className="text-[11px] text-gray-500">
            Postcodes {range.postcode_begin ?? '—'}–{range.postcode_einde ?? '—'} volgen het gebied.
            Een regiotoeslag zet je hierna via Wijzig.
          </p>

          {voorbeeld && (
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-900">
              <div className="font-medium mb-1">Guards akkoord — dit wordt voorgesteld:</div>
              <div>Partner {partnerNaam((voorbeeld.partner_id as string | null) ?? null)} · afhandeling{' '}
                {(voorbeeld.afhandelingsmethode as string | null) ?? '—'} · minimum{' '}
                {euro((voorbeeld.minimum_order_excl_btw as number | undefined) ?? null)} · postcodes{' '}
                {String(voorbeeld.postcode_begin ?? '—')}–{String(voorbeeld.postcode_einde ?? '—')}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={onAnnuleer}>Annuleren</Button>
            {voorbeeld ? (
              <Button size="sm" onClick={bevestigen} isLoading={bezig}>Definitief openstellen</Button>
            ) : (
              <Button size="sm" onClick={controleren} isLoading={bezig}>Controleren</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Segment wijzigen (dpa-toeslag-zetten) ─────────────────────

function WijzigVorm({
  dpa,
  onGedaan,
  onAnnuleer,
}: {
  dpa: DpaRij;
  onGedaan: () => void;
  onAnnuleer: () => void;
}) {
  const [regiotoeslag, setRegiotoeslag] = useState(dpa.regiotoeslag === true);
  const [toeslag, setToeslag] = useState(String(dpa.toeslag ?? ''));
  const [ingangsdatum, setIngangsdatum] = useState('');
  const [minimum, setMinimum] = useState(String(dpa.minimum_order_excl_btw ?? ''));
  const [terugwerkendOk, setTerugwerkendOk] = useState(false);
  const [toonTerugwerkend, setToonTerugwerkend] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { dpaId: dpa.sf_id };
      if (regiotoeslag !== (dpa.regiotoeslag === true)) payload.regiotoeslag = regiotoeslag;
      if (toeslag !== String(dpa.toeslag ?? '')) payload.toeslag = toeslag === '' ? 0 : Number(toeslag);
      if (ingangsdatum) payload.toeslagIngangsdatum = ingangsdatum;
      if (minimum !== String(dpa.minimum_order_excl_btw ?? '')) payload.minimumOrderExclBtw = Number(minimum);
      if (terugwerkendOk) payload.bevestigTerugwerkend = true;
      if (Object.keys(payload).length === 1) { onAnnuleer(); return; }
      await voerVerbUit('dpa-toeslag-zetten', payload);
      onGedaan();
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === 'INGANGSDATUM_TERUGWERKEND') setToonTerugwerkend(true);
      setFout(err.message);
      setBezig(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
      <FoutBanner melding={fout} />
      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input type="checkbox" checked={regiotoeslag} onChange={(e) => setRegiotoeslag(e.target.checked)} className="rounded border-gray-300" />
        Regiotoeslag actief
      </label>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelKlasse}>Toeslag (%)</label>
          <input type="number" min={0} max={100} className={veldKlasse} value={toeslag} onChange={(e) => setToeslag(e.target.value)} />
        </div>
        <div>
          <label className={labelKlasse}>Ingangsdatum</label>
          <input type="date" className={veldKlasse} value={ingangsdatum} onChange={(e) => setIngangsdatum(e.target.value)} />
          <p className="mt-1 text-[11px] text-gray-400">Orders vóór deze datum houden hun prijs.</p>
        </div>
      </div>
      <div>
        <label className={labelKlasse}>Order-minimum (excl. btw)</label>
        <input type="number" min={0} step="0.01" className={veldKlasse} value={minimum} onChange={(e) => setMinimum(e.target.value)} />
      </div>
      {toonTerugwerkend && (
        <label className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <input type="checkbox" checked={terugwerkendOk} onChange={(e) => setTerugwerkendOk(e.target.checked)} className="mt-0.5 rounded border-gray-300" />
          Ik bevestig de terugwerkende ingangsdatum — bestaande offertes en orders vanaf die datum worden bij hun eerstvolgende herprijzing geraakt.
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onAnnuleer}>Annuleren</Button>
        <Button size="sm" onClick={opslaan} isLoading={bezig}>Opslaan</Button>
      </div>
    </div>
  );
}

// ─── Segmenten-modal (welke diensten kunnen we hier verkopen) ───

function SegmentenModal({
  range,
  dpas,
  alleDpas,
  dienstgebieden,
  partners,
  heeftRoutes,
  onClose,
  onMutated,
}: {
  range: RangeRij;
  /** DPA's van dít gebied. */
  dpas: DpaRij[];
  /** Alle DPA's — nodig om erf-waarden uit siblings af te leiden. */
  alleDpas: DpaRij[];
  dienstgebieden: Dienstgebied[];
  partners: PartnerRij[];
  heeftRoutes: boolean;
  onClose: () => void;
  /** Herlaadt de data; het venster blijft open zodat je meerdere kunt doen. */
  onMutated: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null); // dienstgebied sf_id

  const partnerNaam = useCallback(
    (id: string | null) => (id ? partners.find((p) => p.sf_id === id)?.name ?? id : '—'),
    [partners]
  );

  // Segmenten die dit gebied al heeft — de divergentie-guard staat per
  // gebied×segment, dus een tweede dienstgebied met hetzelfde segment wordt
  // sowieso geweigerd. Dat tonen we vooraf in plaats van als foutmelding achteraf.
  const bezetteSegmenten = useMemo(() => {
    const m = new Map<string, DpaRij>();
    for (const d of dpas) if (d.segment) m.set(d.segment, d);
    return m;
  }, [dpas]);

  const rijen = useMemo(
    () =>
      [...dienstgebieden]
        .filter((dg) => dg.segment)
        .sort((a, b) => (a.segment ?? '').localeCompare(b.segment ?? '', 'nl') || (a.name ?? '').localeCompare(b.name ?? '', 'nl')),
    [dienstgebieden]
  );

  return (
    <ModalShell titel={`Segmenten — ${range.name ?? range.sf_id}`} onClose={onClose} breed>
      <p className="text-sm text-gray-600 mb-3">
        Per segment: kunnen we hier verkopen? Zonder prijsassociatie valt een aanvraag in dit gebied
        buiten het werkgebied, ook als er wél een route aan hangt.
      </p>

      {heeftRoutes && !bezetteSegmenten.has(ROUTE_SEGMENT) && (
        <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          Dit gebied is aan een route gekoppeld maar heeft geen {ROUTE_SEGMENT}-prijs. Vloerkleed-aanvragen
          krijgen hier geen offerte en geen ophaaldatum. Stel {ROUTE_SEGMENT} hieronder open.
        </div>
      )}
      {!heeftRoutes && bezetteSegmenten.has(ROUTE_SEGMENT) && (
        <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          Dit gebied heeft een {ROUTE_SEGMENT}-prijs maar geen route-koppeling. Klanten krijgen wel een
          prijs, maar geen ophaaldatum. Koppel een route via de knop Routes.
        </div>
      )}

      <div className="space-y-2">
        {rijen.map((dg) => {
          const eigen = dpas.find((d) => d.dienstgebied_id === dg.sf_id) ?? null;
          const segmentBezetDoor = dg.segment ? bezetteSegmenten.get(dg.segment) : undefined;
          // Ander dienstgebied dekt dit segment al: openstellen zou botsen.
          const geblokkeerd = !eigen && segmentBezetDoor !== undefined;
          const blokkeerderNaam = geblokkeerd
            ? dienstgebieden.find((x) => x.sf_id === segmentBezetDoor?.dienstgebied_id)?.name ?? 'een ander dienstgebied'
            : null;

          return (
            <div key={dg.sf_id} className="border border-gray-200 rounded-md px-3 py-2">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    {dg.segment}
                    <span className="text-gray-400 text-xs"> · {dg.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {eigen ? (
                      <>
                        {partnerNaam(eigen.partner_id)} · {eigen.afhandelingsmethode ?? 'geen afhandeling'} ·
                        minimum {euro(eigen.minimum_order_excl_btw)}
                        {eigen.regiotoeslag && (eigen.toeslag ?? 0) > 0
                          ? ` · toeslag ${eigen.toeslag}%${eigen.toeslag_ingangsdatum ? ` vanaf ${eigen.toeslag_ingangsdatum}` : ''}`
                          : ' · geen toeslag'}
                      </>
                    ) : geblokkeerd ? (
                      <>Segment al gedekt via {blokkeerderNaam}</>
                    ) : (
                      <>Niet ingericht — hier kunnen we dit niet verkopen</>
                    )}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    eigen
                      ? 'bg-green-100 text-green-800'
                      : geblokkeerd
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {eigen ? 'actief' : geblokkeerd ? 'via ander' : 'dicht'}
                </span>
                {eigen ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setOpen(open === dg.sf_id ? null : dg.sf_id)}
                  >
                    Wijzig
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={geblokkeerd}
                    onClick={() => setOpen(open === dg.sf_id ? null : dg.sf_id)}
                  >
                    Openstellen
                  </Button>
                )}
              </div>

              {open === dg.sf_id && eigen && (
                <WijzigVorm
                  dpa={eigen}
                  onGedaan={() => { setOpen(null); onMutated(); }}
                  onAnnuleer={() => setOpen(null)}
                />
              )}
              {open === dg.sf_id && !eigen && !geblokkeerd && (
                <OpenstellenVorm
                  range={range}
                  dienstgebied={dg}
                  erf={bepaalErfWaarden(alleDpas, dg.sf_id)}
                  partnerNaam={partnerNaam}
                  onGedaan={() => { setOpen(null); onMutated(); }}
                  onAnnuleer={() => setOpen(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Sluiten</Button>
      </div>
    </ModalShell>
  );
}

// ─── Routes-modal (rpa-aanmaken / -verhangen / -verwijderen) ───

function RpaModal({
  range,
  rpas,
  routes,
  onClose,
  onSaved,
}: {
  range: RangeRij;
  rpas: RpaRij[];
  routes: RouteDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [fout, setFout] = useState<string | null>(null);
  const [bezigMet, setBezigMet] = useState<string | null>(null); // rpa sf_id | 'nieuw'
  const [bewerkt, setBewerkt] = useState<Record<string, { routeId: string; vanaf: string; tot: string }>>({});
  const [nieuwRoute, setNieuwRoute] = useState('');
  const [nieuwVanaf, setNieuwVanaf] = useState('10:00');
  const [nieuwTot, setNieuwTot] = useState('15:00');

  const routeNaam = useCallback(
    (id: string | null) => routes.find((r) => r.sf_id === id)?.name ?? id ?? '—',
    [routes]
  );

  const stand = (rpa: RpaRij) =>
    bewerkt[rpa.sf_id] ?? {
      routeId: rpa.route_id ?? '',
      vanaf: tijdKort(rpa.tijd_vanaf),
      tot: tijdKort(rpa.tijd_tot),
    };

  const zet = (rpa: RpaRij, patch: Partial<{ routeId: string; vanaf: string; tot: string }>) =>
    setBewerkt((prev) => ({ ...prev, [rpa.sf_id]: { ...stand(rpa), ...patch } }));

  const opslaanBestaand = async (rpa: RpaRij) => {
    const s = stand(rpa);
    setBezigMet(rpa.sf_id);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { rpaId: rpa.sf_id };
      if (s.routeId !== (rpa.route_id ?? '')) payload.routeId = s.routeId;
      if (s.vanaf !== tijdKort(rpa.tijd_vanaf)) payload.tijdVanaf = s.vanaf;
      if (s.tot !== tijdKort(rpa.tijd_tot)) payload.tijdTot = s.tot;
      if (Object.keys(payload).length === 1) { setBezigMet(null); return; }
      await voerVerbUit('rpa-verhangen', payload);
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezigMet(null);
    }
  };

  const verwijderen = async (rpa: RpaRij) => {
    if (!window.confirm(`Koppeling met ${routeNaam(rpa.route_id)} verwijderen? Het gebied krijgt dan geen ophaal-opties meer via die route.`)) return;
    setBezigMet(rpa.sf_id);
    setFout(null);
    try {
      await voerVerbUit('rpa-verwijderen', { rpaId: rpa.sf_id });
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezigMet(null);
    }
  };

  const aanmaken = async () => {
    setBezigMet('nieuw');
    setFout(null);
    try {
      await voerVerbUit('rpa-aanmaken', {
        routeId: nieuwRoute,
        postcodegebiedId: range.sf_id,
        tijdVanaf: nieuwVanaf,
        tijdTot: nieuwTot,
      });
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezigMet(null);
    }
  };

  const tijdVeld = 'h-10 px-2 rounded-md border border-gray-300 text-sm w-24';

  return (
    <ModalShell titel={`Routes — ${range.name ?? range.sf_id}`} onClose={onClose}>
      <FoutBanner melding={fout} />
      <div className="space-y-3">
        {rpas.length === 0 && (
          <p className="text-sm text-gray-500">Geen route-koppelingen — dit gebied krijgt nu géén route-opties.</p>
        )}
        {rpas.map((rpa) => {
          const s = stand(rpa);
          return (
            <div key={rpa.sf_id} className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-2">
              <select className="h-10 px-2 rounded-md border border-gray-300 text-sm flex-1 min-w-0" value={s.routeId} onChange={(e) => zet(rpa, { routeId: e.target.value })}>
                {routes.map((r) => (
                  <option key={r.sf_id} value={r.sf_id}>{r.name}</option>
                ))}
              </select>
              <input type="time" className={tijdVeld} value={s.vanaf} onChange={(e) => zet(rpa, { vanaf: e.target.value })} />
              <span className="text-gray-400 text-sm">–</span>
              <input type="time" className={tijdVeld} value={s.tot} onChange={(e) => zet(rpa, { tot: e.target.value })} />
              <Button size="sm" variant="secondary" onClick={() => opslaanBestaand(rpa)} isLoading={bezigMet === rpa.sf_id}>Opslaan</Button>
              <Button size="sm" variant="danger" onClick={() => verwijderen(rpa)} disabled={bezigMet !== null}>×</Button>
            </div>
          );
        })}
        <div className="border-t border-gray-100 pt-3">
          <div className="text-xs font-medium text-gray-600 mb-2">Nieuwe koppeling</div>
          <div className="flex items-center gap-2">
            <select className="h-10 px-2 rounded-md border border-gray-300 text-sm flex-1 min-w-0" value={nieuwRoute} onChange={(e) => setNieuwRoute(e.target.value)}>
              <option value="">Kies route…</option>
              {routes.map((r) => (
                <option key={r.sf_id} value={r.sf_id}>{r.name}</option>
              ))}
            </select>
            <input type="time" className={tijdVeld} value={nieuwVanaf} onChange={(e) => setNieuwVanaf(e.target.value)} />
            <span className="text-gray-400 text-sm">–</span>
            <input type="time" className={tijdVeld} value={nieuwTot} onChange={(e) => setNieuwTot(e.target.value)} />
            <Button size="sm" onClick={aanmaken} disabled={!nieuwRoute} isLoading={bezigMet === 'nieuw'}>Toevoegen</Button>
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Sluiten</Button>
      </div>
    </ModalShell>
  );
}

// ─── Nieuwe route (route-aanmaken) ─────────────────────────────

function NieuweRouteModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [naam, setNaam] = useState('');
  const [beschrijving, setBeschrijving] = useState('');
  const [ophalen, setOphalen] = useState('');
  const [afleveren, setAfleveren] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { naam };
      if (beschrijving.trim()) payload.beschrijving = beschrijving.trim();
      if (ophalen !== '') payload.capaciteitOphalen = Number(ophalen);
      if (afleveren !== '') payload.capaciteitAfleveren = Number(afleveren);
      await voerVerbUit('route-aanmaken', payload);
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezig(false);
    }
  };

  return (
    <ModalShell titel="Nieuwe route" onClose={onClose}>
      <FoutBanner melding={fout} />
      <div className="space-y-4">
        <div>
          <label className={labelKlasse}>Naam</label>
          <input className={veldKlasse} value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Drenthe" />
        </div>
        <div>
          <label className={labelKlasse}>Beschrijving (optioneel)</label>
          <input className={veldKlasse} value={beschrijving} onChange={(e) => setBeschrijving(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelKlasse}>Capaciteit ophalen (stops)</label>
            <input type="number" min={0} max={99} className={veldKlasse} value={ophalen} onChange={(e) => setOphalen(e.target.value)} />
          </div>
          <div>
            <label className={labelKlasse}>Capaciteit afleveren (stops)</label>
            <input type="number" min={0} max={99} className={veldKlasse} value={afleveren} onChange={(e) => setAfleveren(e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Koppel na het aanmaken gebieden aan de route (klik op de routenaam) — zonder gebieden krijgt de route geen stops.
        </p>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button onClick={opslaan} disabled={!naam.trim()} isLoading={bezig}>Aanmaken</Button>
      </div>
    </ModalShell>
  );
}

// ─── Nieuw gebied (range-aanmaken) ─────────────────────────────

function NieuwGebiedModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [naam, setNaam] = useState('');
  const [begin, setBegin] = useState('');
  const [einde, setEinde] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    setFout(null);
    try {
      await voerVerbUit('range-aanmaken', {
        naam,
        postcodeBegin: Number(begin),
        postcodeEinde: Number(einde),
      });
      onSaved();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
      setBezig(false);
    }
  };

  return (
    <ModalShell titel="Nieuw gebied" onClose={onClose}>
      <FoutBanner melding={fout} />
      <div className="space-y-4">
        <div>
          <label className={labelKlasse}>Naam</label>
          <input className={veldKlasse} value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Hoogeveen/De Wolden" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelKlasse}>Postcode vanaf</label>
            <input type="number" min={1000} max={9999} className={veldKlasse} value={begin} onChange={(e) => setBegin(e.target.value)} />
          </div>
          <div>
            <label className={labelKlasse}>Postcode t/m</label>
            <input type="number" min={1000} max={9999} className={veldKlasse} value={einde} onChange={(e) => setEinde(e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Na het aanmaken: koppel routes (knop Routes) en maak de prijsassociatie aan (knop Toeslag) — zonder die twee is het gebied niet boekbaar.
        </p>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Annuleren</Button>
        <Button onClick={opslaan} disabled={!naam.trim() || !begin || !einde} isLoading={bezig}>Aanmaken</Button>
      </div>
    </ModalShell>
  );
}

// ─── Route-detail (gebieden van één route; zelfde rpa-verbs) ───

function RouteDetailModal({
  route,
  rpas,
  ranges,
  onClose,
  onMutated,
}: {
  route: RouteDef;
  rpas: RpaRij[];
  ranges: RangeRij[];
  onClose: () => void;
  /** Herlaadt de data; het venster blijft open (rijen verversen via props). */
  onMutated: () => void;
}) {
  const [fout, setFout] = useState<string | null>(null);
  const [bezigMet, setBezigMet] = useState<string | null>(null); // rpa sf_id | 'nieuw'
  const [bewerkt, setBewerkt] = useState<Record<string, { vanaf: string; tot: string }>>({});
  const [nieuwGebied, setNieuwGebied] = useState('');
  const [nieuwVanaf, setNieuwVanaf] = useState('10:00');
  const [nieuwTot, setNieuwTot] = useState('15:00');

  const rangeVan = useCallback(
    (id: string | null) => ranges.find((r) => r.sf_id === id),
    [ranges]
  );

  const gesorteerd = useMemo(
    () =>
      [...rpas].sort((a, b) =>
        (rangeVan(a.postcodegebied_id)?.name ?? '').localeCompare(rangeVan(b.postcodegebied_id)?.name ?? '', 'nl')
      ),
    [rpas, rangeVan]
  );

  const gekoppeldeGebieden = useMemo(
    () => new Set(rpas.map((r) => r.postcodegebied_id)),
    [rpas]
  );

  const stand = (rpa: RpaRij) =>
    bewerkt[rpa.sf_id] ?? { vanaf: tijdKort(rpa.tijd_vanaf), tot: tijdKort(rpa.tijd_tot) };

  const zet = (rpa: RpaRij, patch: Partial<{ vanaf: string; tot: string }>) =>
    setBewerkt((prev) => ({ ...prev, [rpa.sf_id]: { ...stand(rpa), ...patch } }));

  const tijdenOpslaan = async (rpa: RpaRij) => {
    const s = stand(rpa);
    setBezigMet(rpa.sf_id);
    setFout(null);
    try {
      const payload: Record<string, unknown> = { rpaId: rpa.sf_id };
      if (s.vanaf !== tijdKort(rpa.tijd_vanaf)) payload.tijdVanaf = s.vanaf;
      if (s.tot !== tijdKort(rpa.tijd_tot)) payload.tijdTot = s.tot;
      if (Object.keys(payload).length > 1) {
        await voerVerbUit('rpa-verhangen', payload);
        setBewerkt((prev) => {
          const next = { ...prev };
          delete next[rpa.sf_id];
          return next;
        });
        onMutated();
      }
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setBezigMet(null);
    }
  };

  const verwijderen = async (rpa: RpaRij) => {
    const gebiedNaam = rangeVan(rpa.postcodegebied_id)?.name ?? rpa.postcodegebied_id;
    if (!window.confirm(`${gebiedNaam} loskoppelen van ${route.name}? Het gebied krijgt dan geen ophaal-opties meer via deze route.`)) return;
    setBezigMet(rpa.sf_id);
    setFout(null);
    try {
      await voerVerbUit('rpa-verwijderen', { rpaId: rpa.sf_id });
      onMutated();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setBezigMet(null);
    }
  };

  const toevoegen = async () => {
    setBezigMet('nieuw');
    setFout(null);
    try {
      await voerVerbUit('rpa-aanmaken', {
        routeId: route.sf_id,
        postcodegebiedId: nieuwGebied,
        tijdVanaf: nieuwVanaf,
        tijdTot: nieuwTot,
      });
      setNieuwGebied('');
      onMutated();
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e));
    } finally {
      setBezigMet(null);
    }
  };

  const tijdVeld = 'h-10 px-2 rounded-md border border-gray-300 text-sm w-24';

  return (
    <ModalShell titel={`Route ${route.name ?? route.sf_id} — ${rpas.length} gebied${rpas.length === 1 ? '' : 'en'}`} onClose={onClose} breed>
      <FoutBanner melding={fout} />
      <div className="space-y-2">
        {gesorteerd.length === 0 && (
          <p className="text-sm text-gray-500">Geen gebieden gekoppeld — deze route krijgt géén stops via route-opties.</p>
        )}
        {gesorteerd.map((rpa) => {
          const s = stand(rpa);
          const gebied = rangeVan(rpa.postcodegebied_id);
          const gewijzigd = s.vanaf !== tijdKort(rpa.tijd_vanaf) || s.tot !== tijdKort(rpa.tijd_tot);
          return (
            <div key={rpa.sf_id} className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 truncate">{gebied?.name ?? rpa.postcodegebied_id}</div>
                <div className="text-xs text-gray-400">
                  {gebied ? `${gebied.postcode_begin ?? '—'}–${gebied.postcode_einde ?? '—'}` : ''}
                </div>
              </div>
              <input type="time" className={tijdVeld} value={s.vanaf} onChange={(e) => zet(rpa, { vanaf: e.target.value })} />
              <span className="text-gray-400 text-sm">–</span>
              <input type="time" className={tijdVeld} value={s.tot} onChange={(e) => zet(rpa, { tot: e.target.value })} />
              <Button size="sm" variant="secondary" onClick={() => tijdenOpslaan(rpa)} disabled={!gewijzigd} isLoading={bezigMet === rpa.sf_id}>
                Opslaan
              </Button>
              <Button size="sm" variant="danger" onClick={() => verwijderen(rpa)} disabled={bezigMet !== null}>×</Button>
            </div>
          );
        })}
        <div className="border-t border-gray-100 pt-3">
          <div className="text-xs font-medium text-gray-600 mb-2">Gebied toevoegen</div>
          <div className="flex items-center gap-2">
            <select
              className="h-10 px-2 rounded-md border border-gray-300 text-sm flex-1 min-w-0"
              value={nieuwGebied}
              onChange={(e) => setNieuwGebied(e.target.value)}
            >
              <option value="">Kies gebied…</option>
              {ranges
                .filter((r) => !gekoppeldeGebieden.has(r.sf_id))
                .map((r) => (
                  <option key={r.sf_id} value={r.sf_id}>
                    {r.name} ({r.postcode_begin ?? '—'}–{r.postcode_einde ?? '—'})
                  </option>
                ))}
            </select>
            <input type="time" className={tijdVeld} value={nieuwVanaf} onChange={(e) => setNieuwVanaf(e.target.value)} />
            <span className="text-gray-400 text-sm">–</span>
            <input type="time" className={tijdVeld} value={nieuwTot} onChange={(e) => setNieuwTot(e.target.value)} />
            <Button size="sm" onClick={toevoegen} disabled={!nieuwGebied} isLoading={bezigMet === 'nieuw'}>Toevoegen</Button>
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Sluiten</Button>
      </div>
    </ModalShell>
  );
}

// ─── Pagina ────────────────────────────────────────────────────

type ModalState =
  | { soort: 'capaciteit'; route: RouteDef }
  | { soort: 'streefduur'; route: RouteDef }
  | { soort: 'route'; routeSfId: string }
  | { soort: 'range'; range: RangeRij }
  | { soort: 'segmenten'; range: RangeRij }
  | { soort: 'rpa'; range: RangeRij }
  | { soort: 'nieuwe-route' }
  | { soort: 'nieuw-gebied' }
  | null;

export function GebiedenPage() {
  const [data, setData] = useState<Overzicht | null>(null);
  const [laadFout, setLaadFout] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');
  /** Actief dekkingsfilter: toon alleen gebieden zónder dit segment. */
  const [zonderSegment, setZonderSegment] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [sortering, setSortering] = useState<{ kolom: 'naam' | 'postcode'; oplopend: boolean }>({
    kolom: 'naam',
    oplopend: true,
  });

  const wisselSortering = (kolom: 'naam' | 'postcode') =>
    setSortering((prev) =>
      prev.kolom === kolom ? { kolom, oplopend: !prev.oplopend } : { kolom, oplopend: true }
    );

  const laden = useCallback(async () => {
    try {
      setData(await haalOverzicht());
      setLaadFout(null);
    } catch (e) {
      setLaadFout(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const naOpslaan = useCallback(() => {
    setModal(null);
    void laden();
  }, [laden]);

  const dpasPerGebied = useMemo(() => {
    const m = new Map<string, DpaRij[]>();
    for (const d of data?.dpas ?? []) {
      if (!d.postcode_gebied_id) continue;
      const list = m.get(d.postcode_gebied_id) ?? [];
      list.push(d);
      m.set(d.postcode_gebied_id, list);
    }
    return m;
  }, [data]);

  const rpasPerGebied = useMemo(() => {
    const m = new Map<string, RpaRij[]>();
    for (const r of data?.rpas ?? []) {
      if (!r.postcodegebied_id) continue;
      const list = m.get(r.postcodegebied_id) ?? [];
      list.push(r);
      m.set(r.postcodegebied_id, list);
    }
    return m;
  }, [data]);

  /** Segmenten die dit gebied heeft, uit zijn eigen DPA's. */
  const segmentenPerGebied = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const d of data?.dpas ?? []) {
      if (!d.postcode_gebied_id || !d.segment) continue;
      const s = m.get(d.postcode_gebied_id) ?? new Set<string>();
      s.add(d.segment);
      m.set(d.postcode_gebied_id, s);
    }
    return m;
  }, [data]);

  /** Segmenten die breed genoeg zijn om overal als chip te tonen, inclusief waar
   *  ze ontbreken — dat gat is precies wat je wilt zien. */
  const kernSegmenten = useMemo(() => {
    const telling = new Map<string, number>();
    for (const [, segs] of segmentenPerGebied) {
      for (const s of segs) telling.set(s, (telling.get(s) ?? 0) + 1);
    }
    return [...telling.entries()]
      .filter(([, n]) => n >= KERN_SEGMENT_DREMPEL)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'nl'))
      .map(([s]) => s);
  }, [segmentenPerGebied]);

  const rpasPerRoute = useMemo(() => {
    const m = new Map<string, RpaRij[]>();
    for (const r of data?.rpas ?? []) {
      if (!r.route_id) continue;
      const list = m.get(r.route_id) ?? [];
      list.push(r);
      m.set(r.route_id, list);
    }
    return m;
  }, [data]);

  const routeNaam = useCallback(
    (id: string | null) => data?.routes.find((r) => r.sf_id === id)?.name ?? '—',
    [data]
  );

  const gefilterdeRanges = useMemo(() => {
    if (!data) return [];
    const q = zoek.trim().toLowerCase();
    const basis = zonderSegment
      ? data.ranges.filter((r) => !(segmentenPerGebied.get(r.sf_id) ?? new Set()).has(zonderSegment))
      : data.ranges;
    const gefilterd = !q
      ? basis
      : basis.filter((r) => {
          if ((r.name ?? '').toLowerCase().includes(q)) return true;
          const alsGetal = Number(q);
          if (Number.isInteger(alsGetal) && r.postcode_begin !== null && r.postcode_einde !== null) {
            return alsGetal >= r.postcode_begin && alsGetal <= r.postcode_einde;
          }
          const routesVan = rpasPerGebied.get(r.sf_id) ?? [];
          return routesVan.some((rpa) => routeNaam(rpa.route_id).toLowerCase().includes(q));
        });
    const richting = sortering.oplopend ? 1 : -1;
    return [...gefilterd].sort((a, b) => {
      if (sortering.kolom === 'postcode') {
        // Rijen zonder postcode altijd onderaan, ongeacht de richting.
        const av = a.postcode_begin;
        const bv = b.postcode_begin;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * richting;
      }
      return (a.name ?? '').localeCompare(b.name ?? '', 'nl') * richting;
    });
  }, [data, zoek, zonderSegment, segmentenPerGebied, rpasPerGebied, routeNaam, sortering]);

  if (laadFout) {
    return (
      <div className="flex-1 p-6">
        <FoutBanner melding={laadFout} />
        <Button variant="secondary" onClick={() => void laden()}>Opnieuw proberen</Button>
      </div>
    );
  }
  if (!data) {
    return <div className="flex-1 flex items-center justify-center"><Spinner /></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Gebieden &amp; routes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Werkgebieden, route-koppelingen, regiotoeslagen en capaciteit. Wijzigingen zijn direct actief
            voor prijsberekening en route-opties.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>Routes</CardTitle>
              <Button size="sm" variant="secondary" onClick={() => setModal({ soort: 'nieuwe-route' })}>
                Nieuwe route
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Route</th>
                  <th className="py-2 pr-4 font-medium">Gebieden</th>
                  <th className="py-2 pr-4 font-medium">Cap. ophalen</th>
                  <th className="py-2 pr-4 font-medium">Cap. afleveren</th>
                  <th className="py-2 pr-4 font-medium">Streefduur routedag</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {data.routes.map((route) => (
                  <tr key={route.sf_id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4">
                      <button
                        className="text-gray-900 hover:text-accent hover:underline text-left"
                        onClick={() => setModal({ soort: 'route', routeSfId: route.sf_id })}
                      >
                        {route.name}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{(rpasPerRoute.get(route.sf_id) ?? []).length}</td>
                    <td className="py-2 pr-4">{route.capaciteit_ophalen ?? '—'}</td>
                    <td className="py-2 pr-4">{route.capaciteit_afleveren ?? '—'}</td>
                    <td className="py-2 pr-4"><button className="text-accent hover:underline" aria-label={`Streefduur wijzigen voor ${route.name}`} onClick={() => setModal({ soort: 'streefduur', route })}>{formatStreefduur(route.dag_budget_minuten)}</button></td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setModal({ soort: 'capaciteit', route })}>
                        Capaciteit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>Gebieden ({gefilterdeRanges.length})</CardTitle>
              <div className="flex items-center gap-2">
                <input
                  className="h-9 px-3 rounded-md border border-gray-300 text-sm w-64"
                  placeholder="Zoek op naam, postcode of route…"
                  value={zoek}
                  onChange={(e) => setZoek(e.target.value)}
                />
                <Button size="sm" variant="secondary" onClick={() => setModal({ soort: 'nieuw-gebied' })}>
                  Nieuw gebied
                </Button>
              </div>
            </div>
            {/* Dekkingsfilter: waar staat een segment dicht? Dat is de vraag die
                je anders alleen per gebied één voor één kunt beantwoorden. */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-xs text-gray-500">Toon gebieden zonder:</span>
              {kernSegmenten.map((seg) => {
                const aantal = data.ranges.filter(
                  (r) => !(segmentenPerGebied.get(r.sf_id) ?? new Set()).has(seg)
                ).length;
                const actief = zonderSegment === seg;
                return (
                  <button
                    key={seg}
                    onClick={() => setZonderSegment(actief ? null : seg)}
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium border ${
                      actief
                        ? 'bg-accent text-white border-accent'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {seg} ({aantal})
                  </button>
                );
              })}
              {zonderSegment && (
                <button className="text-xs text-gray-500 underline hover:text-gray-800" onClick={() => setZonderSegment(null)}>
                  filter wissen
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">
                    <button
                      className="inline-flex items-center gap-1 hover:text-gray-800"
                      onClick={() => wisselSortering('naam')}
                    >
                      Gebied{sortering.kolom === 'naam' ? (sortering.oplopend ? ' ▲' : ' ▼') : ''}
                    </button>
                  </th>
                  <th className="py-2 pr-4 font-medium">
                    <button
                      className="inline-flex items-center gap-1 hover:text-gray-800"
                      onClick={() => wisselSortering('postcode')}
                    >
                      Postcodes{sortering.kolom === 'postcode' ? (sortering.oplopend ? ' ▲' : ' ▼') : ''}
                    </button>
                  </th>
                  <th className="py-2 pr-4 font-medium">Routes</th>
                  <th className="py-2 pr-4 font-medium">Segmenten</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {gefilterdeRanges.map((range) => {
                  const dpas = dpasPerGebied.get(range.sf_id) ?? [];
                  const rpas = rpasPerGebied.get(range.sf_id) ?? [];
                  const eigenSegmenten = segmentenPerGebied.get(range.sf_id) ?? new Set<string>();
                  // Kernsegmenten altijd tonen (ook als gat), plus wat dit gebied
                  // verder nog heeft (Matras e.d.).
                  const chips = [
                    ...kernSegmenten,
                    ...[...eigenSegmenten].filter((s) => !kernSegmenten.includes(s)).sort(),
                  ];
                  const routeZonderPrijs = rpas.length > 0 && !eigenSegmenten.has(ROUTE_SEGMENT);
                  const prijsZonderRoute = rpas.length === 0 && eigenSegmenten.has(ROUTE_SEGMENT);
                  const omgekeerd =
                    range.postcode_begin !== null && range.postcode_einde !== null &&
                    range.postcode_begin > range.postcode_einde;
                  const heeftVenster = range.gesloten_van !== null && range.gesloten_tot !== null;
                  return (
                    <tr key={range.sf_id} className="border-b border-gray-100 last:border-0 align-top">
                      <td className="py-2 pr-4 text-gray-900">
                        {range.name}
                        {heeftVenster && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                            title={range.gesloten_reden ?? undefined}
                          >
                            ⛔ dicht {range.gesloten_van} t/m {range.gesloten_tot}
                          </span>
                        )}
                        {routeZonderPrijs && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                            title={`Route gekoppeld maar geen ${ROUTE_SEGMENT}-prijs: aanvragen vallen hier buiten het werkgebied`}
                          >
                            route zonder prijs
                          </span>
                        )}
                        {prijsZonderRoute && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                            title={`${ROUTE_SEGMENT}-prijs zonder route-koppeling: klant krijgt wel een prijs maar geen ophaaldatum`}
                          >
                            prijs zonder route
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {range.postcode_begin ?? '—'}–{range.postcode_einde ?? '—'}
                        {omgekeerd && (
                          <span className="ml-2 text-xs text-red-600 font-medium" title="Begin ligt boven einde — gebied is onvindbaar voor route-opties en pricing">
                            omgekeerd!
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {rpas.length === 0 ? (
                          <span className="text-gray-400">geen</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {rpas.map((rpa) => (
                              <span key={rpa.sf_id} className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                {routeNaam(rpa.route_id)} {tijdKort(rpa.tijd_vanaf)}–{tijdKort(rpa.tijd_tot)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {chips.map((seg) => {
                            const dpa = dpas.find((d) => d.segment === seg);
                            const actief = dpa !== undefined;
                            const toeslag = actief && dpa.regiotoeslag === true && (dpa.toeslag ?? 0) > 0
                              ? ` +${dpa.toeslag}%`
                              : '';
                            const titel = actief
                              ? `${seg}: minimum ${euro(dpa.minimum_order_excl_btw)} · ${dpa.afhandelingsmethode ?? 'geen afhandeling'}` +
                                (toeslag ? ` · toeslag${toeslag}` : ' · geen toeslag')
                              : `${seg} staat dicht in dit gebied — klik om open te stellen`;
                            return (
                              <button
                                key={seg}
                                title={titel}
                                onClick={() => setModal({ soort: 'segmenten', range })}
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
                                  actief
                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                    : 'border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600'
                                }`}
                              >
                                {seg}{toeslag}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setModal({ soort: 'rpa', range })}>Routes</Button>
                        <Button size="sm" variant="ghost" onClick={() => setModal({ soort: 'segmenten', range })}>Segmenten</Button>
                        <Button size="sm" variant="ghost" onClick={() => setModal({ soort: 'range', range })}>Bewerken</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {modal?.soort === 'streefduur' && (
        <StreefduurModal route={modal.route} onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
      {modal?.soort === 'capaciteit' && (
        <CapaciteitModal route={modal.route} onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
      {modal?.soort === 'route' && (() => {
        const route = data.routes.find((r) => r.sf_id === modal.routeSfId);
        if (!route) return null;
        return (
          <RouteDetailModal
            route={route}
            rpas={rpasPerRoute.get(route.sf_id) ?? []}
            ranges={data.ranges}
            onClose={() => setModal(null)}
            onMutated={() => void laden()}
          />
        );
      })()}
      {modal?.soort === 'range' && (
        <RangeModal range={modal.range} onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
      {modal?.soort === 'segmenten' && (
        <SegmentenModal
          range={modal.range}
          dpas={dpasPerGebied.get(modal.range.sf_id) ?? []}
          alleDpas={data.dpas}
          dienstgebieden={data.gebieden}
          partners={data.partners ?? []}
          heeftRoutes={(rpasPerGebied.get(modal.range.sf_id) ?? []).length > 0}
          onClose={() => setModal(null)}
          onMutated={() => void laden()}
        />
      )}
      {modal?.soort === 'nieuwe-route' && (
        <NieuweRouteModal onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
      {modal?.soort === 'nieuw-gebied' && (
        <NieuwGebiedModal onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
      {modal?.soort === 'rpa' && (
        <RpaModal range={modal.range} rpas={rpasPerGebied.get(modal.range.sf_id) ?? []} routes={data.routes} onClose={() => setModal(null)} onSaved={naOpslaan} />
      )}
    </div>
  );
}
